<template>
  <!-- The envelope (S17, AP2 mandates): a standing yes the member signs in
       this browser, the ledger of everything done under it, and the proof —
       every signature checked here, on this page, not taken on our word. -->
  <main class="page env-page">
    <p v-if="loading" class="lab">Reading the envelope…</p>

    <template v-else>
      <header class="top">
        <div>
          <span class="lab">The envelope · signed mandates</span>
          <h1 class="d2 head">{{ headline }}</h1>
        </div>
        <span v-if="m && m.status === 'active'" class="rstamp filed">Signed</span>
        <span v-else-if="m && m.status === 'suspended'" class="rstamp returned">Suspended</span>
        <span v-else-if="view && !view.gate.earned" class="rstamp held">Not yet</span>
      </header>

      <p class="lede intro">
        A standing yes, signed by you, for a few people and a few occasions. Inside it I can order a gift on my own &mdash; after
        telling you what and when, with time to stop me, and a real undo after. Outside it, everything still waits on your yes.
      </p>

      <div v-if="loadError" class="panel sober"><p class="sm">{{ loadError }}</p></div>

      <div v-else-if="view && !view.available" class="panel kraftp">
        <p class="sm"><strong>Not switched on yet.</strong> Envelopes need our side&#8217;s signing key, and it isn&#8217;t there. Until it is, every gift waits on your yes &mdash; as it does now.</p>
      </div>

      <div v-if="view" class="cols">
        <div class="main">
          <!-- Suspended: a gift it chose alone missed. -->
          <div v-if="m && m.status === 'suspended'" class="panel sober suspended">
            <span class="lab red">Suspended {{ fmtDate(m.suspended && m.suspended.at) }}</span>
            <p class="sm gap">Something I chose on my own missed, so nothing more goes out under this envelope. That&#8217;s the rule I signed up to. Sign a new one when you&#8217;re ready &mdash; or don&#8217;t.</p>
          </div>

          <!-- What was signed -->
          <section v-if="m" class="panel carbon signed" :class="{ green: m.status === 'active' }">
            <span class="cc">Your copy · signed {{ fmtDate(m.issuedAt) }} · until {{ fmtDate(m.validUntil) }}</span>
            <p class="d4 gap words">&#8220;{{ m.description }}&#8221;</p>
            <div class="meter gap" :aria-label="`${dollars(m.spentCents)} of ${dollars(m.envelope.budgetCents)} used this quarter`">
              <div class="bar"><span :style="{ width: `${Math.min(100, Math.round((m.spentCents / m.envelope.budgetCents) * 100))}%` }"></span></div>
              <p class="xs">{{ dollars(m.spentCents) }} of {{ dollars(m.envelope.budgetCents) }} used this quarter ({{ m.quarter }}), counting what&#8217;s waiting out its notice.</p>
            </div>
            <dl class="slip gap">
              <div><dt>Signed by</dt><dd>{{ signedHere ? 'the key in this browser' : 'a key on another device or browser' }} <code class="did" :title="m.holderDid">{{ shortDid(m.holderDid) }}</code></dd></div>
              <div><dt>Acting for you</dt><dd>our agent <code class="did" :title="view.agentDid">{{ shortDid(view.agentDid) }}</code></dd></div>
            </dl>
            <div class="btnrow gap">
              <button type="button" class="btn" :disabled="verifying" @click="verifyAll">{{ verifying ? 'Checking…' : 'Check every signature' }}</button>
              <button v-if="m.status === 'active'" type="button" class="btn red" :disabled="busy" @click="revoke">Tear it up</button>
            </div>
            <p v-if="intentCheck" class="sm gap check" :class="intentCheck.ok ? 'ok' : 'bad'">
              {{ intentCheck.ok ? '✓ Your signature on the envelope checks out, here in this browser.' : `✗ The envelope doesn’t check out: ${intentCheck.why}.` }}
            </p>
          </section>

          <!-- The ledger -->
          <div v-if="m" class="divider gapped"><span class="dtab">Under it</span><span class="cnt">{{ view.ledger.length }} {{ view.ledger.length === 1 ? 'gift' : 'gifts' }}</span></div>
          <p v-if="m && !view.ledger.length" class="sm mut">Nothing yet. When I choose something under it you&#8217;ll hear first, with the date it goes.</p>
          <ol v-if="m && view.ledger.length" class="ledger">
            <li v-for="r in view.ledger" :key="r.proposalId" class="row">
              <div class="row-head">
                <router-link :to="`/gifts/${r.proposalId}`" class="d4 what">{{ r.title }}</router-link>
                <span class="pill" :class="stateCls(r.state)">{{ stateWords(r) }}</span>
              </div>
              <p class="xs">
                For {{ r.name || 'someone' }} · held up to {{ dollars(r.holdCents) }}<template v-if="r.chargedCents != null"> · charged {{ dollars(r.chargedCents) }}</template>
                · {{ r.state === 'noticed' ? `goes ${fmtWhen(r.ordersAt)} unless you stop it` : fmtDate(r.at) }}
              </p>
              <p v-if="chains[r.proposalId]" class="xs chain">
                <span v-for="s in chains[r.proposalId].steps" :key="s.step" :class="s.ok ? 'ok' : 'bad'">{{ s.ok ? '✓' : '✗' }} {{ STEP_WORDS[s.step] }}{{ s.ok ? '' : ` (${s.why})` }}</span>
              </p>
            </li>
          </ol>

          <!-- Signing -->
          <section v-if="canSign" class="panel sign">
            <span class="lab">{{ m && m.status === 'active' ? 'Sign a new one (it replaces this)' : 'Sign an envelope' }}</span>
            <form class="gap" @submit.prevent="sign">
              <div class="grid2">
                <label class="fl">A quarter, at most
                  <select v-model.number="draft.budgetCents"><option v-for="c in BUDGETS" :key="c" :value="c">{{ dollars(c) }}</option></select>
                </label>
                <label class="fl">One gift, at most
                  <select v-model.number="draft.perGiftCents"><option v-for="c in PER_GIFT" :key="c" :value="c" :disabled="c > draft.budgetCents">{{ dollars(c) }}</option></select>
                </label>
              </div>

              <span class="fl gap">For</span>
              <div class="chiprow">
                <button v-for="p in people" :key="p.id" type="button" class="chip" :aria-pressed="draft.people.includes(p.id) ? 'true' : 'false'" @click="toggle('people', p.id)">
                  <span class="box" aria-hidden="true"></span>{{ p.name }}
                </button>
              </div>
              <p v-if="!people.length" class="xs">Add someone to your people first.</p>

              <span class="fl gap">When</span>
              <div class="chiprow">
                <button v-for="o in OCCASIONS" :key="o.key" type="button" class="chip" :aria-pressed="draft.occasions.includes(o.key) ? 'true' : 'false'" @click="toggle('occasions', o.key)">
                  <span class="box" aria-hidden="true"></span>{{ o.label }}
                </button>
              </div>

              <span class="fl gap">What</span>
              <div class="chiprow">
                <button v-for="o in LANES" :key="o.key" type="button" class="chip" :aria-pressed="draft.lanes.includes(o.key) ? 'true' : 'false'" @click="toggle('lanes', o.key)">
                  <span class="box" aria-hidden="true"></span>{{ o.label }}
                </button>
              </div>

              <div class="grid2 gap">
                <label class="fl">Notice before I order
                  <select v-model.number="draft.noticeHours"><option v-for="h in [24, 48, 72]" :key="h" :value="h">{{ h }} hours</option></select>
                </label>
                <label class="fl">Lasts
                  <select v-model.number="draft.validDays"><option v-for="d in [90, 180, 365]" :key="d" :value="d">{{ d === 365 ? 'a year' : `${d / 30} months` }}</option></select>
                </label>
              </div>

              <label class="opt gap"><input v-model="draft.theirYes" type="checkbox" /> <span class="sm">If they have their own agent, it has to say yes outright</span></label>

              <div class="preview gap">
                <span class="lab sub">What you&#8217;d be signing</span>
                <p class="sm theirwords">&#8220;{{ preview }}&#8221;</p>
              </div>

              <div class="btnrow gap">
                <button type="submit" class="btn" :disabled="busy || !draftValid">{{ busy ? 'Signing…' : 'Sign with this browser' }}</button>
              </div>
              <p class="xs gap">Your signature is made by a key that lives in this browser and can&#8217;t be copied out of it &mdash; not by this page, not by us. Anyone can check it.</p>
              <p v-if="signError" class="sm gap bad">{{ signError }}</p>
            </form>
          </section>
        </div>

        <aside class="side">
          <!-- The gate: authority follows judgement. -->
          <div class="panel sober gate">
            <span class="lab" :class="view.gate.earned ? 'green' : ''">{{ view.gate.earned ? 'Earned' : 'Has it earned this?' }}</span>
            <ul class="checks">
              <li v-for="c in view.gate.checks" :key="c.key" :class="c.ok ? 'ok' : 'bad'"><span aria-hidden="true">{{ c.ok ? '✓' : '·' }}</span> {{ c.text }}</li>
            </ul>
            <p class="xs gap">Only judgement earns autonomy. Until I&#8217;ve held back more than I&#8217;ve sent, and you&#8217;ve agreed with how, there&#8217;s nothing to sign.</p>
            <div v-if="!view.gate.earned" class="btnrow gap"><router-link to="/assistant" class="btn ghost small">Rate my holds on the desk →</router-link></div>
          </div>

          <div class="panel kraftp">
            <span class="lab">The three signatures</span>
            <dl class="slip gap">
              <div><dt>Intent</dt><dd>Yours. The envelope: who, what, how much, how much notice.</dd></div>
              <div><dt>Cart</dt><dd>Ours. One gift, chosen inside it, citing your signature, with the most it may cost and when it goes.</dd></div>
              <div><dt>Payment</dt><dd>Ours. The money held for that cart, citing it.</dd></div>
            </dl>
            <p class="xs gap">The shape is AP2&#8217;s, as W3C Verifiable Credentials. A gift it chose alone that misses suspends the envelope.</p>
          </div>
        </aside>
      </div>
    </template>
  </main>
</template>

<script>
import { db, auth } from '@/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getEnvelope, draftEnvelope, signEnvelope, revokeEnvelope } from '@/api';
import { supported, memberKey, hasMemberKey, signInput, didFor, verify, verifyChain } from '@/utils/envelopeKey';

const BUDGETS = [5000, 10000, 20000, 30000, 50000];
const PER_GIFT = [2500, 4000, 6000, 10000, 15000];
const OCCASIONS = [
  { key: 'birthday', label: 'Birthdays', words: 'birthdays' },
  { key: 'anniversary', label: 'Anniversaries', words: 'anniversaries' },
  { key: 'need', label: 'A need I notice', words: "a need you'd want met" },
  { key: 'other', label: 'Other occasions', words: 'other occasions' },
];
const LANES = [
  { key: 'made', label: 'Made for them' },
  { key: 'bought', label: 'Bought from a shop' },
];
const STEP_WORDS = { intent: 'your envelope', cart: 'our cart, citing it', payment: 'our payment, citing the cart' };
const STATE_WORDS = {
  noticed: 'Waiting out its notice', ordering: 'Being ordered', paid: 'Paid for', ordered: 'Ordered',
  stopped: 'You stopped it', lapsed: 'Went back to you', failed: "Didn't go through", undone: 'Undone',
};

const dollars = (c) => `$${(c / 100).toFixed(c % 100 ? 2 : 0)}`;
const listed = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

export default {
  name: 'EnvelopePage',
  data() {
    return {
      BUDGETS, PER_GIFT, OCCASIONS, LANES, STEP_WORDS,
      uid: null, loading: true, loadError: '', view: null, people: [],
      draft: { budgetCents: 20000, perGiftCents: 6000, people: [], occasions: ['birthday'], lanes: ['made', 'bought'], noticeHours: 48, validDays: 90, theirYes: true },
      busy: false, signError: '', verifying: false, intentCheck: null, chains: {}, localDid: null, authUnsub: null,
    };
  },
  computed: {
    m() { return this.view && this.view.mandate; },
    headline() {
      if (!this.view) return 'The envelope.';
      if (this.m && this.m.status === 'active') return 'I can act alone, inside this.';
      if (this.m && this.m.status === 'suspended') return 'Suspended. Something I chose alone missed.';
      if (!this.view.gate.earned) return 'Not yet. I haven’t earned it.';
      return 'You can let me act alone, inside an envelope.';
    },
    canSign() {
      return Boolean(this.view && this.view.available && this.view.gate.earned && supported());
    },
    signedHere() {
      return Boolean(this.m && this.localDid && this.localDid === this.m.holderDid);
    },
    draftValid() {
      const d = this.draft;
      return d.people.length && d.occasions.length && d.lanes.length && d.perGiftCents <= d.budgetCents;
    },
    // Mirrors describeEnvelope() in functions/shared/mandate/mandate.js; the
    // server's own wording is what gets signed.
    preview() {
      const d = this.draft;
      const names = this.people.filter((p) => d.people.includes(p.id)).map((p) => String(p.name).split(/\s+/)[0]);
      const lanes = d.lanes.length === 2 ? 'made or bought' : d.lanes[0] === 'made' ? 'made for them' : 'bought';
      const occ = OCCASIONS.filter((o) => d.occasions.includes(o.key)).map((o) => o.words);
      return `Up to ${dollars(d.budgetCents)} a quarter, at most ${dollars(d.perGiftCents)} a gift, ${lanes}, for ${listed(names) || '…'}, ` +
        `for ${listed(occ) || '…'}. ${d.noticeHours} hours' notice before anything is ordered, and I can be stopped until then.` +
        (d.theirYes ? ' If they have their own agent, it has to say yes.' : '');
    },
  },
  created() {
    this.authUnsub = onAuthStateChanged(auth, (user) => {
      this.uid = user ? user.uid : null;
      if (user) this.load();
    });
  },
  beforeUnmount() {
    if (this.authUnsub) this.authUnsub();
  },
  methods: {
    dollars,
    async load() {
      this.loading = !this.view;
      try {
        const [res, rels] = await Promise.all([
          getEnvelope(),
          getDocs(query(collection(db, 'relationships'), where('userId', '==', this.uid), where('status', '==', 'active'))),
        ]);
        this.view = res.data;
        this.people = rels.docs.map((d) => ({ id: d.id, name: d.data().name || 'Someone' })).sort((a, b) => a.name.localeCompare(b.name));
        if (this.m && !this.draft.people.length) this.draft.people = (this.m.envelope.people || []).map((p) => p.id);
        if (await hasMemberKey(this.uid)) this.localDid = didFor((await memberKey(this.uid)).publicJwk);
      } catch (e) {
        this.loadError = 'Couldn’t read the envelope just now.';
      } finally {
        this.loading = false;
      }
    },
    toggle(field, key) {
      const xs = this.draft[field];
      this.draft[field] = xs.includes(key) ? xs.filter((x) => x !== key) : [...xs, key];
    },
    async sign() {
      this.busy = true;
      this.signError = '';
      try {
        const key = await memberKey(this.uid);
        const env = { ...this.draft, people: this.draft.people.map((id) => ({ id })) };
        const { data: drafted } = await draftEnvelope({ envelope: env, holderJwk: key.publicJwk });
        const signature = await signInput(key, drafted.signingInput);
        await signEnvelope({ draftId: drafted.draftId, signature });
        this.localDid = didFor(key.publicJwk);
        this.intentCheck = null;
        this.chains = {};
        await this.load();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        this.signError = (e && e.message) || 'That didn’t sign.';
      } finally {
        this.busy = false;
      }
    },
    async revoke() {
      this.busy = true;
      try {
        await revokeEnvelope({ mandateId: this.m.id });
        this.intentCheck = null;
        this.chains = {};
        await this.load();
      } finally {
        this.busy = false;
      }
    },
    async verifyAll() {
      this.verifying = true;
      try {
        const intent = await verify(this.m.intentJws);
        this.intentCheck = intent.ok && intent.issuer === this.m.holderDid ? { ok: true } : { ok: false, why: intent.why || 'signed by a different key' };
        const chains = {};
        for (const r of this.view.ledger) {
          chains[r.proposalId] = await verifyChain({
            intentJws: this.m.intentJws, holderDid: this.m.holderDid, agentDid: this.view.agentDid, cartJws: r.cartJws, paymentJws: r.paymentJws,
          });
        }
        this.chains = chains;
      } finally {
        this.verifying = false;
      }
    },
    stateWords(r) {
      return STATE_WORDS[r.state] || r.state;
    },
    stateCls(state) {
      if (['ordered', 'paid'].includes(state)) return 'ok';
      if (['failed', 'undone', 'stopped'].includes(state)) return 'no';
      return '';
    },
    shortDid(did) {
      return did ? `${did.slice(0, 14)}…${did.slice(-6)}` : '';
    },
    fmtDate(ms) {
      return ms ? new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).toLowerCase() : '';
    },
    fmtWhen(ms) {
      return ms ? new Date(ms).toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' }) : '';
    },
  },
};
</script>

<style scoped>
.top { display: flex; flex-wrap: wrap; gap: 14px 24px; align-items: center; }
.top .rstamp { margin-left: auto; }
.head { margin-top: 8px; }
.intro { margin-top: 14px; max-width: 64ch; }
.gap { margin-top: 12px; }
.cols { display: grid; gap: clamp(28px, 3.6vw, 48px); margin-top: 30px; }
@media (min-width: 1000px) { .cols { grid-template-columns: minmax(0, 1.5fr) minmax(260px, .75fr); align-items: start; } }
.main > .panel + .panel, .main > .panel + section, .side > .panel + .panel { margin-top: 18px; }
.suspended { margin-bottom: 18px; }
.words { max-width: 52ch; }
.meter .bar { height: 10px; border: 1.5px solid var(--ink); background: var(--sheet); }
.meter .bar span { display: block; height: 100%; background: var(--red); }
.meter .xs { margin-top: 6px; }
.slip { display: grid; gap: 6px; margin: 0; }
.slip div { display: grid; grid-template-columns: 7.5em 1fr; gap: 10px; }
.slip dt { font-family: var(--mono, monospace); font-size: .72rem; text-transform: uppercase; letter-spacing: .06em; color: var(--ink-3); padding-top: 2px; }
.slip dd { margin: 0; overflow-wrap: anywhere; }
.did { font-size: .72rem; background: var(--sunk); padding: 1px 5px; }
.check.ok, .chain .ok, .checks .ok { color: var(--green); }
.check.bad, .chain .bad, .bad { color: var(--red-ink); }
.divider.gapped { margin-top: 36px; }
.ledger { list-style: none; margin: 10px 0 0; padding: 0; }
.ledger .row { border-top: 1px solid var(--rule); padding: 14px 0; }
.row-head { display: flex; gap: 12px; align-items: baseline; justify-content: space-between; flex-wrap: wrap; }
.what { color: var(--ink); text-decoration: none; overflow-wrap: anywhere; }
.what:hover { text-decoration: underline; text-underline-offset: 4px; }
.pill.ok { border-color: var(--green); color: var(--green); }
.pill.no { border-color: var(--red-ink); color: var(--red-ink); }
.chain { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: 6px; }
.sign { margin-top: 28px; }
.grid2 { display: grid; gap: 12px; }
@media (min-width: 560px) { .grid2 { grid-template-columns: 1fr 1fr; } }
.grid2 select { display: block; width: 100%; margin-top: 6px; }
.fl { display: block; }
.opt { display: flex; gap: 10px; align-items: center; cursor: pointer; }
.opt input { width: auto; flex: none; margin: 0; }
.preview { border-left: 3px solid var(--red); padding-left: 12px; }
.checks { list-style: none; margin: 10px 0 0; padding: 0; display: grid; gap: 8px; }
.checks li { display: grid; grid-template-columns: 1.1em 1fr; gap: 6px; font-size: .9rem; }
.checks .bad { color: var(--ink-2); }
</style>
